import { useEffect } from 'react';
import { Button, Skeleton } from '@radix-ui/themes';
import { Link, useParams, useNavigate } from 'react-router-dom';
import FITUrls from 'helpers/FITUrls';
import { isCurrentUserAdmin } from 'helpers/authentication';
import { confirmationModal } from 'helpers/confirmation';
import { getSurfaceType } from 'helpers/utils';
import AdKaoraBanner, { AdSlot } from 'ui/design-base/AdKaoraBanner';
import CompetitionObject from 'ui/design-base/CompetitionObject';
import DrawBestLoserRankingModifyBox from 'ui/design-base/DrawBestLoserRankingModifyBox';
import DrawTurnContainer from 'ui/design-base/DrawTurnContainer';
import DrawWinnerModifyBox from 'ui/design-base/DrawWinnerModifyBox';
import ErrorBox from 'ui/design-base/ErrorBox';
import NoMenuHeader from 'ui/design-base/NoMenuHeader';
import Page from 'ui/design-base/Page';
import PlayerUserObject from 'ui/design-base/PlayerUserObject';
import { deleteDraw, deleteMatches, fetchDraw } from 'store/draw/drawSlice';
import { useAppDispatch, useAppSelector } from 'store/store';
import { deleteMatch, fetchMatchResult } from 'store/updateMatchResult/updateMatchResultSlice';
import type { MatchDrawType } from 'types/match';

export const DrawPage = () => {
  const dispatch = useAppDispatch();
  const { draw, loading, error } = useAppSelector((state) => state.draw);
  const { id } = useParams<string>();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchDraw(id));
  }, [id, dispatch]);

  const getPageDescriptionChampionship = () =>
    `Pagina dell'incontro : ${draw?.name}  - della fase : ${
      draw?.tournament?.name || ''
    } - della Competizione a squadre: ${draw?.competition?.name || ''} - Numero Partite: ${
      draw?.matches?.length
    } - Scoprile Tutte! `;

  const getPageDescription = () =>
    `Pagina del Tabellone: ${draw?.name}  - del Torneo: ${draw?.tournament?.name || ''} - della Competizione: ${
      draw?.competition?.name || ''
    } - Vinto da: ${draw?.winner?.full_name || ''} - Numero Partite: ${draw?.matches?.length} - Scoprile Tutte! `;

  const getPageKeyword = () =>
    `Tabellone: ${draw?.name} | Torneo: ${draw?.tournament?.name || ''} | Competizione: ${
      draw?.competition?.name || ''
    } | Vincitore: ${draw?.winner?.full_name || ''} | Numero Partite: ${draw?.matches?.length}`;

  const onDeleteMatch = (match: { is_deletable: boolean; id: number }) => {
    const { is_deletable } = match;
    if (is_deletable) {
      confirmationModal('Sei sicuro di voler cancellare questo match?', () => {
        dispatch(
          deleteMatch({
            id: match.id,
            onCallback: () => {
              dispatch(fetchDraw(id));
            },
          }),
        );
      });
    }
  };

  const editMatchResult = ({ is_editable, id, type }: { is_editable: boolean; id: number; type: string }) => {
    if (is_editable) {
      dispatch(fetchMatchResult({ id, type }));
    }
  };

  const insertMatchButton = () =>
    isCurrentUserAdmin() ? (
      <Button className='cursor-pointer' onClick={() => navigate(`/tabellone/${id}/aggiunta-partita`)}>
        Aggiungi
      </Button>
    ) : null;

  const deleteMatchesButton = () =>
    isCurrentUserAdmin() ? (
      <Button
        className='cursor-pointer'
        onClick={() => {
          confirmationModal('Sei sicuro di voler cancellare tutte le partite di questo tabellone?', () => {
            dispatch(deleteMatches(id));
          });
        }}
      >
        Cancella Partite
      </Button>
    ) : null;

  const deleteDrawButton = () =>
    isCurrentUserAdmin() ? (
      <Button
        className='cursor-pointer'
        onClick={() => {
          confirmationModal('Sei sicuro di voler cancellare questo tabellone e tutte le sue partite?', () => {
            dispatch(deleteDraw(id));
          });
        }}
      >
        Cancella Tabellone
      </Button>
    ) : null;

  const destructurateMatches: (_matches?: MatchDrawType[]) => MatchDrawType[][] | undefined = (
    matches?: MatchDrawType[],
  ) =>
    matches
      ?.reduce((turns: { [key: string]: any }, match: MatchDrawType) => {
        let key = match['round_id'];
        if (!key) key = 0;
        if (!turns[key]) {
          turns[key] = [];
        }
        turns[key].push(match);

        return turns;
      }, [])
      .filter(Boolean)
      .reverse();

  const getLabelTurn = (i: number) => {
    if (i === 0) return 'finale';
    else if (i === 1) return 'semifinale';
    else if (i === 2) return 'quarti';
    else if (i === 3) return 'ottavi';
    else {
      const s = Math.pow(2, i);
      return `${s}-esimi`;
    }
  };

  const renderContent = () => {
    const turns = destructurateMatches(draw?.matches);
    const itemClassName =
      'grow bg-white self-stretch gap-2 p-3 rounded-xl border border-solid border-neutral-neutral-2';
    const labelClassName = 'self-stretch text-neutral-neutral-8 text-sm font-medium tracking-normal';
    const contentClassName =
      'flex justify-between self-stretch text-neutral-neutral-alpha-12 text-lg font-bold tracking-tighter leading-9 md:leading-10';

    return (
      <>
        <NoMenuHeader
          loading={loading}
          {...draw}
          defaultName='Tabellone FITP'
          fitpLink={draw?.competition?.championship ? '' : FITUrls.draw(draw?.fit_draw_id, draw?.tournament)}
        />
        <div className='mt-3'>
          <div
            id='info-container'
            className='border-neutral-neutral-2 bg-neutral-neutral-1 flex flex-wrap gap-2 rounded-xl border border-solid p-3'
          >
            <Skeleton loading={loading} className='h-auto w-auto'>
              <div className='grow gap-2 self-stretch pt-3'>
                <div className={labelClassName}>Competizione</div>
                <div className='pt-2'>
                  <CompetitionObject {...draw?.competition} loading={loading} />
                </div>
              </div>
            </Skeleton>
            <div className='grow gap-2 self-stretch pt-3'>
              <div className={labelClassName}>Torneo</div>
              <Skeleton loading={loading} className='h-auto w-auto'>
                <div className='text-neutral-neutral-alpha-12 flex justify-between self-stretch text-base leading-9 font-bold tracking-tighter md:leading-10'>
                  <Link
                    className='border-neutral-neutral-2 hover:bg-accent-accent-3 mt-2 grow self-stretch rounded-xl border border-solid bg-white px-3 py-1'
                    to={`/torneo/${draw?.tournament.id}`}
                  >
                    {draw?.tournament.name}
                  </Link>
                </div>
              </Skeleton>
            </div>

            {draw?.competition?.championship ? (
              <div className={itemClassName}>
                <div className={labelClassName}>Data</div>
                <Skeleton loading={loading} className='h-auto w-auto'>
                  <div className={contentClassName}>
                    {draw.match_date && new Date(draw.match_date).toLocaleDateString()}
                  </div>
                </Skeleton>
              </div>
            ) : (
              <div className='grow gap-2 self-stretch pt-3'>
                <div className={labelClassName}>Vincitore</div>
                <Skeleton loading={loading} className='h-auto w-auto'>
                  {draw?.winner ? (
                    <div className='border-accent-accent-6 bg-accent-accent-3 mt-2 rounded-md border border-solid font-bold'>
                      <PlayerUserObject {...draw?.winner} type='fit_player_profile' loading={loading} />
                    </div>
                  ) : (
                    '-'
                  )}
                  <div className='flex flex-col gap-2'>
                    {isCurrentUserAdmin() && <DrawWinnerModifyBox id={id} />}
                    {isCurrentUserAdmin() && draw && (
                      <DrawBestLoserRankingModifyBox id={id} bestLoserRanking={draw?.best_loser_ranking} />
                    )}
                  </div>
                </Skeleton>
              </div>
            )}
            {draw?.competition?.championship && draw?.home_club?.name && (
              <div className={itemClassName}>
                <div className={labelClassName}>Circolo di casa</div>
                <Skeleton loading={loading} className='h-auto w-auto'>
                  <div className={contentClassName}>
                    <Link to={`/circolo/${draw?.home_club?.id}`}>{draw?.home_club?.name}</Link>
                  </div>
                </Skeleton>
              </div>
            )}
            {draw?.competition?.championship && draw?.away_club?.name && (
              <div className={itemClassName}>
                <div className={labelClassName}>Circolo ospite</div>
                <Skeleton loading={loading} className='h-auto w-auto'>
                  <div className={contentClassName}>
                    <Link to={`/circolo/${draw?.away_club?.id}`}>{draw?.away_club?.name}</Link>
                  </div>
                </Skeleton>
              </div>
            )}
            {draw?.competition?.championship && (
              <div className={itemClassName}>
                <div className={labelClassName}>Risultato</div>
                <Skeleton loading={loading} className='h-auto w-auto'>
                  <div className={contentClassName}>{draw?.score}</div>
                </Skeleton>
              </div>
            )}
            {draw?.competition?.championship && draw?.surface && (
              <div className={itemClassName}>
                <div className={labelClassName}>Superfice</div>
                <Skeleton loading={loading} className='h-auto w-auto'>
                  <div className={contentClassName}>{getSurfaceType(draw.surface)}</div>
                </Skeleton>
              </div>
            )}
            {draw?.competition?.championship && draw?.ball_type && (
              <div className={itemClassName}>
                <div className={labelClassName}>Tipologia di palle</div>
                <Skeleton loading={loading} className='h-auto w-auto'>
                  <div className={contentClassName}>{draw.ball_type}</div>
                </Skeleton>
              </div>
            )}
            {draw?.subcribers_count != null && draw?.subcribers_count !== 0 && (
              <div className={itemClassName}>
                <div className={labelClassName}>Numero di iscritti</div>
                <Skeleton loading={loading} className='h-auto w-auto'>
                  <div className='text-neutral-neutral-alpha-12 flex justify-between self-stretch text-2xl font-bold'>
                    {draw?.subcribers_count}
                  </div>
                </Skeleton>
              </div>
            )}
            {draw?.entries_count != null && draw?.entries_count !== 0 && (
              <div className={itemClassName}>
                <div className={labelClassName}>Numero di entranti</div>
                <Skeleton loading={loading} className='h-auto w-auto'>
                  <div className={contentClassName}>{draw?.entries_count}</div>
                </Skeleton>
              </div>
            )}
            {draw?.outer_count != null && draw?.outer_count !== 0 && (
              <div className={itemClassName}>
                <div className={labelClassName}>Numero di uscenti</div>
                <Skeleton loading={loading} className='h-auto w-auto'>
                  <div className={contentClassName}>{draw?.outer_count}</div>
                </Skeleton>
              </div>
            )}
            {draw?.head_series_count != null && draw?.head_series_count !== 0 && (
              <div className={itemClassName}>
                <div className={labelClassName}>Numero teste di serie</div>
                <Skeleton loading={loading} className='h-auto w-auto'>
                  <div className={contentClassName}>{draw?.head_series_count}</div>
                </Skeleton>
              </div>
            )}
          </div>
        </div>
        <div>
          {isCurrentUserAdmin() && (
            <div className='mt-3 flex items-center justify-between gap-3'>
              {insertMatchButton()} {deleteMatchesButton()} {deleteDrawButton()}
            </div>
          )}

          {turns?.map((turn: MatchDrawType[], i: number) => (
            <div className='pt-3' key={i}>
              <DrawTurnContainer
                loading={loading}
                competitionName={`Turno ${getLabelTurn(i)}`}
                matches={turn}
                onDeleteMatch={onDeleteMatch}
                editMatchResult={editMatchResult}
              />
            </div>
          ))}
        </div>
      </>
    );
  };

  let pageTitle = 'Tabellone';
  let pageDescription = pageTitle;
  let pageKeywords;
  let content = renderContent();
  if (!loading) {
    if (!error && draw) {
      pageTitle = draw.name;
      pageDescription = draw?.competition?.championship ? getPageDescriptionChampionship() : getPageDescription();
      pageKeywords = getPageKeyword();
    } else {
      content = (
        <div className='flex justify-center'>
          <ErrorBox
            errorType='Tabellone non trovato'
            errorTitle='Qualcosa è andato storto nel caricamento di questo tabellone'
            errorMessage='Prova più tardi. Se avrai nuovamente problemi, scrivici.'
          />
        </div>
      );
    }
  }

  return (
    <Page title={pageTitle} description={pageDescription} keywords={pageKeywords} showAds={true}>
      <AdKaoraBanner slot={AdSlot.ARTICLE_TOP} />
      {content}
      <AdKaoraBanner slot={AdSlot.ARTICLE_BOTTOM} />
    </Page>
  );
};

export default DrawPage;
